import React, { useState, useEffect } from "react";
import {
  CreditCard,
  Heart,
  User,
  Mail,
  Phone,
  MapPin,
  IndianRupee,
  Check,
  Loader,
  AlertCircle,
  Shield,
  Landmark,
  Flame,
  HandCoins,
  ScrollText,
  Sparkles,
  Building2,
} from "lucide-react";

const DonationScreen = () => {
  const [selectedAmount, setSelectedAmount] = useState("");
  const [selectedSeva, setSelectedSeva] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [donationType, setDonationType] = useState("general");
  const [paymentMethod, setPaymentMethod] = useState("online");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    panCard: "",
    donationPurpose: "",
  });
  const [donationSuccess, setDonationSuccess] = useState(null);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);

  // ✅ HARD-CODED PRODUCTION CONFIGURATION
  const API_BASE_URL = "https://srikalikamundeshwari.com";
  const RAZORPAY_KEY_ID = "rzp_live_RcOu7sPz4MvGiH";
  const TEMPLE_NAME = "Sri Kalikamundeshwari Temple";

  const predefinedAmounts = ["5", "501", "1001", "2001", "5001", "11000"];

  const sevaOptions = [
    { id: "kumkum-archana", label: "Kumkum archana", amount: "51" },
    { id: "flower-archana", label: "Flower Archana", amount: "201" },
    { id: "sahasra-namarchana", label: "Sahasra Namarchana", amount: "101" },
    {
      id: "panchamritha-abhisekam",
      label: "Panchamritha Abhisekam",
      amount: "251",
    },
    { id: "ksheera-payasa-seva", label: "Ksheera payasa seva", amount: "251" },
    { id: "pongal-seva", label: "Pongal seva", amount: "251" },
    { id: "maha-pooja", label: "Maha Pooja", amount: "2501" },
    { id: "alankara-seva", label: "Alankara seva", amount: "2501" },
    { id: "amavasya-homa", label: "Amavasya Homa", amount: "5001" },
    {
      id: "amavasya-devi-prasannum",
      label: "Amavasya Devi prasannum",
      amount: "101",
    },
    {
      id: "sri-kali-namaskaram",
      label: "Sri Kali Namaskaram Dristi pariharam",
      amount: "151",
    },
    {
      id: "maha-kalasha-abhisekam",
      label: "Maha Kalasha Abhisekam",
      amount: "10001",
    },
    {
      id: "deviparayana-depa-durga-puja",
      label: "Deviparayana Depa Durga Puja",
      amount: "501",
    },
    {
      id: "anna-dhanam-sevan",
      label: "Anna dhanam Sevan (specially amavasya)",
      amount: "5001",
    },
  ];

  const constructionOptions = [
    {
      id: "temple-construction-per-square-feet",
      label: "Temple construction Per square feet donation",
      amount: "6897",
    },
    { id: "one-bag-cement", label: "One bag cement", amount: "331" },
    { id: "five-bag-cement", label: "Five bag cement", amount: "1651" },
    { id: "ten-bag-cement", label: "Ten bag cement", amount: "3301" },
    { id: "one-unit-sand", label: "One unit sand", amount: "5001" },
    { id: "bricks-100-nos", label: "Bricks 100 nos", amount: "801" },
    { id: "bricks-500-nos", label: "Bricks 500 nos", amount: "4001" },
    { id: "bricks-1000-nos", label: "Bricks 1000 nos", amount: "8001" },
    {
      id: "devatha-adhara-peetam",
      label: "Devatha Adhara PEETAM",
      amount: "10001",
    },
    {
      id: "devatha-one-set-doors",
      label: "Devatha one set Doors",
      amount: "15001",
    },
  ];

  const donationTypes = [
    { id: "general", name: "General Donation", icon: HandCoins },
    { id: "pooja", name: "Special Pooja", icon: Flame },
    { id: "annadanam", name: "Annadanam (Food)", icon: Sparkles },
    { id: "construction", name: "Temple Construction", icon: Building2 },
    { id: "education", name: "Religious Education", icon: ScrollText },
    { id: "festival", name: "Festival Celebration", icon: Landmark },
  ];

  // Load Razorpay Script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        if (window.Razorpay) {
          setRazorpayLoaded(true);
          resolve(true);
          return;
        }

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.onload = () => {
          console.log("✅ Razorpay SDK loaded successfully");
          setRazorpayLoaded(true);
          resolve(true);
        };
        script.onerror = () => {
          console.error("❌ Failed to load Razorpay SDK");
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleDonationTypeSelect = (typeId) => {
    setDonationType(typeId);
    setSelectedSeva("");
    setSelectedAmount("");
    setCustomAmount("");
    setError("");
  };

  const handleSevaSelect = (sevaId) => {
    const selected = [...sevaOptions, ...constructionOptions].find(
      (option) => option.id === sevaId,
    );
    setSelectedSeva(sevaId);
    setSelectedAmount(selected?.amount || "");
    setCustomAmount("");
    setError("");
  };

  const handleAmountSelect = (amount) => {
    setSelectedSeva("");
    setSelectedAmount(amount);
    setCustomAmount("");
    setError("");
  };

  const handleCustomAmountChange = (e) => {
    setCustomAmount(e.target.value);
    setSelectedAmount("");
    setError("");
  };

  const getFinalAmount = () => {
    return selectedAmount || customAmount;
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError("Please enter your full name");
      return false;
    }

    if (!formData.email.trim()) {
      setError("Please enter your email address");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address");
      return false;
    }

    if (!formData.phone.trim()) {
      setError("Please enter your phone number");
      return false;
    }

    const phoneRegex = /^[6-9]\d{9}$/;
    if (!phoneRegex.test(formData.phone.replace(/\D/g, ""))) {
      setError("Please enter a valid 10-digit phone number");
      return false;
    }

    const amountStr = getFinalAmount();
    const amount = parseFloat(amountStr);

    if (!amountStr || isNaN(amount) || amount <= 0) {
      setError("Please select or enter a valid donation amount");
      return false;
    }

    if (amount < 1) {
      setError("Minimum donation amount is ₹1");
      return false;
    }

    return true;
  };

  // Create Razorpay Order
  const createRazorpayOrder = async (amount) => {
    try {
      console.log("📤 Creating Razorpay order...");
      console.log("💰 Amount:", amount);
      console.log("🌐 API URL:", `${API_BASE_URL}/api/razorpay/create-order`);

      const payload = {
        amount: Number(amount),
        currency: "INR",
        receipt: `donation_${Date.now()}`,
        notes: {
          donationType: donationType,
          name: formData.name,
          email: formData.email,
        },
      };

      console.log("📦 Order payload:", payload);

      const response = await fetch(
        `${API_BASE_URL}/api/razorpay/create-order`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        },
      );

      console.log("📥 Order response status:", response.status);
      console.log("📥 Order response headers:", response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Order creation failed:", errorText);
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json();
      console.log("📋 Order result:", result);

      if (!result.success) {
        throw new Error(result.message || "Failed to create payment order");
      }

      console.log("✅ Order created successfully:", result.orderId);
      return result;
    } catch (error) {
      console.error("❌ Create order error:", error);
      throw error;
    }
  };

  // Submit Donation to Database (SKIP VERIFICATION)
  const submitDonation = async (
    razorpayOrderId,
    razorpayPaymentId,
    razorpaySignature,
    amount,
  ) => {
    try {
      console.log("💾 Submitting donation to database...");
      console.log("🌐 API URL:", `${API_BASE_URL}/api/donation`);

      const donationData = {
        ...formData,
        amount: Number(amount),
        donationType: donationType,
        paymentMethod: "razorpay",
        paymentStatus: "completed", // Mark as completed since payment succeeded
        razorpayOrderId: razorpayOrderId,
        razorpayPaymentId: razorpayPaymentId,
        razorpaySignature: razorpaySignature,
        transactionRef: razorpayPaymentId,
      };

      console.log("📦 Donation payload:", donationData);

      const response = await fetch(`${API_BASE_URL}/api/donation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(donationData),
      });

      console.log("📥 Donation response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Donation submission failed:", errorText);
        throw new Error(`Failed to save donation: ${response.status}`);
      }

      const result = await response.json();
      console.log("📋 Donation result:", result);

      if (!result.success) {
        throw new Error(result.message || "Failed to process donation");
      }

      console.log("✅ Donation saved successfully");
      return result;
    } catch (error) {
      console.error("❌ Donation API error:", error);
      throw error;
    }
  };

  // Handle Razorpay Payment
  const handleRazorpayPayment = async (amount) => {
    try {
      console.log("🚀 Starting Razorpay payment process...");

      // Create order
      const orderData = await createRazorpayOrder(amount);

      if (!orderData.success) {
        throw new Error("Failed to create payment order");
      }

      // Razorpay options
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: TEMPLE_NAME,
        description: `${
          donationTypes.find((t) => t.id === donationType)?.name || "Donation"
        }`,
        image: "/temple-logo.png",
        order_id: orderData.orderId,
        prefill: {
          name: formData.name,
          email: formData.email,
          contact: formData.phone,
        },
        notes: {
          donationType: donationType,
          address: formData.address,
        },
        theme: {
          color: "#ea580c",
        },
        handler: async function (response) {
          console.log("✅ Payment successful:", response);
          console.log("🔹 Order ID:", response.razorpay_order_id);
          console.log("🔹 Payment ID:", response.razorpay_payment_id);
          console.log("🔹 Signature:", response.razorpay_signature);

          try {
            // SKIP VERIFICATION - Go directly to save donation
            console.log(
              "⚠️ Skipping verification, saving donation directly...",
            );

            const result = await submitDonation(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature,
              amount,
            );

            setDonationSuccess({
              ...result,
              amount: Number(amount),
              receiptNumber: result.receiptNumber,
              transactionId: response.razorpay_payment_id,
            });
          } catch (error) {
            console.error("❌ Post-payment error:", error);
            // Even if saving fails, payment succeeded - show payment ID
            setError(
              `Payment successful but failed to save. Please contact support with Payment ID: ${response.razorpay_payment_id}`,
            );
          } finally {
            setIsLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            console.log("⚠️ Payment cancelled by user");
            setIsLoading(false);
            setError("Payment cancelled. Please try again.");
          },
        },
      };

      console.log("🎯 Opening Razorpay checkout...");
      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.open();

      razorpayInstance.on("payment.failed", function (response) {
        console.error("❌ Payment failed:", response.error);
        setIsLoading(false);
        setError(`Payment failed: ${response.error.description}`);
      });
    } catch (error) {
      console.error("❌ Payment initiation error:", error);
      setIsLoading(false);
      setError(error.message || "Failed to initiate payment");
    }
  };

  // Handle Submit
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const amount = parseFloat(getFinalAmount());

      if (paymentMethod === "online") {
        // Check if Razorpay is loaded
        if (!razorpayLoaded || !window.Razorpay) {
          throw new Error(
            "Payment gateway not loaded. Please refresh and try again.",
          );
        }

        // Process Razorpay payment
        await handleRazorpayPayment(amount);
      } else {
        // For offline payments, submit directly
        const donationData = {
          ...formData,
          amount: Number(amount),
          donationType: donationType,
          paymentMethod: "offline",
          paymentStatus: "pending",
        };

        const result = await submitDonation(null, null, null, amount);

        setDonationSuccess({
          ...result,
          amount: Number(amount),
          receiptNumber: result.receiptNumber,
          offline: true,
        });
        setIsLoading(false);
      }
    } catch (error) {
      console.error("❌ Submit error:", error);
      setError(
        error.message || "Failed to process donation. Please try again.",
      );
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setDonationSuccess(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      address: "",
      panCard: "",
      donationPurpose: "",
    });
    setSelectedAmount("");
    setSelectedSeva("");
    setCustomAmount("");
    setDonationType("general");
    setPaymentMethod("online");
    setError("");
    setIsLoading(false);
  };

  // Success Screen
  if (donationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-100 to-red-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center animate-slideUp">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            Donation Successful!
          </h2>
          <p className="text-gray-600 mb-4">
            Thank you for your generous contribution to {TEMPLE_NAME}
          </p>

          <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
            <p className="text-sm text-gray-600 mb-2">
              <strong>Amount:</strong>{" "}
              <span className="text-green-600 font-semibold">
                ₹{donationSuccess.amount}
              </span>
            </p>
            {donationSuccess.receiptNumber && (
              <p className="text-sm text-gray-600 mb-2">
                <strong>Receipt No:</strong> {donationSuccess.receiptNumber}
              </p>
            )}
            {donationSuccess.transactionId && (
              <p className="text-sm text-gray-600 mb-2">
                <strong>Transaction ID:</strong> {donationSuccess.transactionId}
              </p>
            )}
            {donationSuccess.offline && (
              <p className="text-sm text-orange-600 mt-2">
                <strong>Note:</strong> Please visit the temple counter to
                complete your offline donation.
              </p>
            )}
          </div>

          <p className="text-sm text-gray-500 mb-6">
            A receipt has been sent to your email address. Please check your
            inbox.
          </p>

          <button
            onClick={resetForm}
            className="bg-orange-600 text-white px-6 py-2 rounded-lg hover:bg-orange-700 transition-colors"
          >
            Make Another Donation
          </button>
        </div>
      </div>
    );
  }

  // Main Donation Form
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 to-red-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 animate-slideUp">
          <div className="w-16 h-16 bg-gradient-to-r from-orange-600 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Make a Donation
          </h1>
          <p className="text-gray-600">
            Support {TEMPLE_NAME} with your generous contribution
          </p>
          <p className="text-black-600 mt-2">
            <b>"There will be no 80G or tax exemption provided to donors."</b>
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-xl p-8 animate-slideUp">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {/* Donation Type Selection */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">
              Select Donation Purpose
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {donationTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleDonationTypeSelect(type.id)}
                    className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                      donationType === type.id
                        ? "border-orange-500 bg-orange-50 transform scale-105"
                        : "border-gray-200 hover:border-orange-300"
                    }`}
                  >
                    <div className="mb-2 flex justify-center">
                      <Icon
                        className="h-7 w-7 text-orange-600"
                        strokeWidth={1.8}
                      />
                    </div>
                    <div className="text-sm font-medium">{type.name}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Amount Selection */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">
              {donationType === "pooja" || donationType === "construction"
                ? donationType === "pooja"
                  ? "Select Seva"
                  : "Select Temple Construction Option"
                : "Select Amount"}
            </h3>

            {donationType === "pooja" || donationType === "construction" ? (
              <>
                <div className="mb-4">
                  <select
                    value={selectedSeva}
                    onChange={(e) => handleSevaSelect(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="">
                      {donationType === "pooja"
                        ? "Choose a Seva"
                        : "Choose a temple construction option"}
                    </option>
                    {(donationType === "pooja"
                      ? sevaOptions
                      : constructionOptions
                    ).map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedSeva && (
                  <div className="relative">
                    <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={
                        (donationType === "pooja"
                          ? sevaOptions
                          : constructionOptions
                        ).find((option) => option.id === selectedSeva)
                          ?.amount || ""
                      }
                      readOnly
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-4">
                  {predefinedAmounts.map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => handleAmountSelect(amount)}
                      className={`p-3 rounded-lg border-2 transition-all duration-300 ${
                        selectedAmount === amount
                          ? "border-orange-500 bg-orange-50 transform scale-105"
                          : "border-gray-200 hover:border-orange-300"
                      }`}
                    >
                      <IndianRupee className="w-4 h-4 inline mr-1" />
                      {amount}
                    </button>
                  ))}
                </div>

                {/* Custom Amount */}
                <div className="relative">
                  <IndianRupee className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="number"
                    placeholder="Enter custom amount"
                    value={customAmount}
                    onChange={handleCustomAmountChange}
                    min="1"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </>
            )}
          </div>

          {/* Personal Details */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">
              Personal Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name *"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number *"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>

              <div className="relative">
                <CreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  name="panCard"
                  placeholder="PAN Card (Optional)"
                  value={formData.panCard}
                  onChange={handleInputChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-4 relative">
              <MapPin className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
              <textarea
                name="address"
                placeholder="Address"
                value={formData.address}
                onChange={handleInputChange}
                rows="3"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>

            <div className="mt-4">
              <textarea
                name="donationPurpose"
                placeholder="Special message or purpose (Optional)"
                value={formData.donationPurpose}
                onChange={handleInputChange}
                rows="3"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Payment Method */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold mb-4 text-gray-800">
              Payment Method
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setPaymentMethod("online")}
                className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                  paymentMethod === "online"
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:border-orange-300"
                }`}
              >
                <Shield className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                <div className="font-medium">Razorpay Payment</div>
                <div className="text-sm text-gray-500">
                  UPI, Card, Net Banking
                </div>
                <div className="text-xs text-green-600 mt-1 flex items-center justify-center">
                  <Shield className="w-3 h-3 mr-1" />
                  Secure Payment
                </div>
              </button>

              <button
                type="button"
                onClick={() => setPaymentMethod("offline")}
                className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                  paymentMethod === "offline"
                    ? "border-orange-500 bg-orange-50"
                    : "border-gray-200 hover:border-orange-300"
                }`}
              >
                <CreditCard className="w-8 h-8 mx-auto mb-2 text-orange-600" />
                <div className="font-medium">Cash / Cheque</div>
                <div className="text-sm text-gray-500">
                  Pay at Temple Counter
                </div>
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={
              isLoading ||
              !getFinalAmount() ||
              !formData.name ||
              !formData.email ||
              !formData.phone
            }
            className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-orange-700 hover:to-red-700 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <Loader className="w-5 h-5 animate-spin mr-2" />
                Processing Payment...
              </>
            ) : (
              <>
                {paymentMethod === "online"
                  ? "Proceed to Payment"
                  : "Submit Donation Request"}
                {getFinalAmount() && (
                  <span className="ml-2">- ₹{getFinalAmount()}</span>
                )}
              </>
            )}
          </button>

          {/* Razorpay Badge */}
          {paymentMethod === "online" && (
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 flex items-center justify-center">
                <Shield className="w-3 h-3 mr-1" />
                Secured by Razorpay | All payment methods supported
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DonationScreen;
